import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import ArticlesList from '../components/ArticlesList';
import { apiService, Article, Author, resolveStrapiMediaUrl } from '../services/api';
import colors from '../theme/colors';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.5rem 2rem;
  background-color: ${colors.lightGray};
  min-height: calc(100vh - 200px);
`;

const BackLink = styled(Link)`
  color: ${colors.mainOrange};
  text-decoration: none;
  font-weight: 500;
`;

const Header = styled.div`
  margin: 1rem 0 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Avatar = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 50%;
  border: 2px solid ${colors.goldGrey};
`;

const AuthorTitle = styled.h1`
  margin: 0;
  color: ${colors.mainBlack};
  font-size: clamp(1.5rem, 3vw, 2rem);
`;

const Subtext = styled.p`
  margin: 0.25rem 0 0;
  color: ${colors.textGray};
`;

const ErrorMessage = styled.div`
  color: ${colors.mainOrange};
  background-color: rgba(255, 86, 30, 0.08);
  border: 1px solid ${colors.mainOrange};
  border-radius: 8px;
  padding: 1rem;
`;

const Loading = styled.div`
  color: ${colors.textGray};
  padding: 1rem 0;
`;

const AuthorDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [author, setAuthor] = useState<Author | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const decoded = decodeURIComponent(slug);
        const foundAuthor = await apiService.getAuthorBySlug(decoded);
        if (!foundAuthor) {
          setError('Автора не знайдено');
          setAuthor(null);
          setArticles([]);
          return;
        }
        setAuthor(foundAuthor);
        const response = await apiService.getArticlesByAuthor(decoded);
        setArticles(Array.isArray(response.data) ? response.data : []);
      } catch (e: any) {
        setError(e?.message || 'Не вдалося завантажити автора');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [slug]);

  const avatarUrl = resolveStrapiMediaUrl(author?.avatar?.url);

  return (
    <PageContainer>
      <BackLink to="/">← На головну</BackLink>

      {loading ? (
        <Loading>Завантаження...</Loading>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <>
          <Header>
            {avatarUrl ? <Avatar src={avatarUrl} alt={author?.name || 'Автор'} /> : null}
            <div>
              <AuthorTitle>{author?.name || 'Автор'}</AuthorTitle>
              {author?.email ? <Subtext>{author.email}</Subtext> : null}
            </div>
          </Header>
          <ArticlesList articles={articles} />
        </>
      )}
    </PageContainer>
  );
};

export default AuthorDetail;
